import { unwrap, type TotpEnrolment, type TotpStatus } from '@qrkey/api-client';
import { otpCodeSchema } from '@qrkey/contracts';
import { Alert, Button, Spinner, TextField, useToast } from '@qrkey/ui';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../app/api.js';
import { errorMessage } from '../lib/errors.js';
import { signOut } from '../lib/sign-out.js';
import { AuthLayout } from './AuthLayout.js';

/** The authenticator app we recommend on the enrolment screen. */
const KEYTHENTIC_URL = 'https://keythentic.com/';

/**
 * Owner/Manager back-office sign-in needs TOTP; the first sign-in gets a session that can
 * only enrol (docs/dev.md "Back office"). Enrol, confirm with a code, keep the recovery
 * codes, then sign in again with the code.
 */
export function TotpEnrolScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string>();
  const [done, setDone] = useState<TotpStatus | null>(null);

  const enrol = useMutation({
    mutationFn: (): Promise<TotpEnrolment> => unwrap(api.client.POST('/v1/auth/totp/enrol')),
  });
  const verify = useMutation({
    mutationFn: (c: string) =>
      unwrap(api.client.POST('/v1/auth/totp/verify', { body: { code: c } })),
    onSuccess: setDone,
  });

  // The otpauth:// link as a scannable QR (a PNG data URL, drawn on the device).
  const otpauthUrl = enrol.data?.otpauthUrl;
  const [qr, setQr] = useState<string>();
  useEffect(() => {
    if (otpauthUrl === undefined) return;
    let live = true;
    QRCode.toDataURL(otpauthUrl, { margin: 2, scale: 6 }).then(
      (url) => live && setQr(url),
      () => live && setQr(undefined),
    );
    return () => {
      live = false;
    };
  }, [otpauthUrl]);

  // Once per screen (StrictMode runs effects twice in development).
  const started = useRef(false);
  const start = enrol.mutate;
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start();
  }, [start]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!otpCodeSchema.safeParse(code).success) {
      setCodeError('Enter the 6-digit code');
      return;
    }
    setCodeError(undefined);
    verify.mutate(code);
  }

  async function finish() {
    await signOut();
    toast.show({
      tone: 'success',
      title: 'Two-step verification is on',
      description: 'Sign in again with a code.',
    });
    void navigate({ to: '/login' });
  }

  if (done) {
    return (
      <AuthLayout
        title="Keep your recovery codes"
        description="Each code signs you in once if you lose your phone. They are shown only now."
      >
        <div className="stack">
          <ul className="recovery-codes qk-num" aria-label="Recovery codes">
            {done.recoveryCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(done.recoveryCodes.join('\n')).then(
                () => toast.show({ tone: 'success', title: 'Copied' }),
                () => toast.show({ tone: 'warning', title: 'Could not copy; write them down' }),
              );
            }}
          >
            Copy codes
          </Button>
          <Button size="lg" block onClick={() => void finish()}>
            I saved them, sign in again
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Turn on two-step verification"
      description="Your role needs a code from an authenticator app (Keythentic, Google Authenticator, Microsoft Authenticator, 1Password…) for the back office."
    >
      <form className="stack" onSubmit={onSubmit} noValidate>
        {enrol.isPending ? <Spinner label="Preparing" /> : null}
        {enrol.isError ? <Alert tone="danger">{errorMessage(enrol.error)}</Alert> : null}
        {enrol.data ? (
          <>
            <ol className="steps">
              <li>
                Install an authenticator app on your phone. We recommend{' '}
                <a href={KEYTHENTIC_URL} target="_blank" rel="noopener noreferrer">
                  Keythentic
                </a>
                .
              </li>
              <li>
                In the app, add an account and scan this code.
                <div className="totp-qr-card">
                  {qr ? (
                    <img
                      className="totp-qr"
                      src={qr}
                      alt="QR code for your authenticator app"
                      data-testid="totp-qr"
                    />
                  ) : (
                    <Spinner label="Drawing the QR code" />
                  )}
                </div>
                <details className="totp-manual">
                  <summary>Can’t scan it?</summary>
                  <p>
                    On this phone, <a href={enrol.data.otpauthUrl}>open it in the app</a>, or enter
                    this key:
                  </p>
                  <code className="secret qk-num" data-testid="totp-secret">
                    {enrol.data.secret.replace(/(.{4})/g, '$1 ').trim()}
                  </code>
                </details>
              </li>
              <li>Enter the 6-digit code the app shows.</li>
            </ol>
            {verify.isError ? (
              <Alert tone="danger">{errorMessage(verify.error, 'otp')}</Alert>
            ) : null}
            <TextField
              label="Code from the app"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              error={codeError}
              inputSize="lg"
              className="qk-num code-input"
            />
            <Button type="submit" size="lg" block loading={verify.isPending}>
              Turn on
            </Button>
          </>
        ) : null}
        <Button
          variant="ghost"
          onClick={() => void signOut().then(() => navigate({ to: '/login' }))}
        >
          Cancel and sign out
        </Button>
      </form>
    </AuthLayout>
  );
}
